<template>
  <section class="section is-medium faq-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.faq-title') }}
        </h2>
      </div>
      
      <div class="columns is-centered">
        <div class="column is-10 is-8-desktop">
          <div class="faq-list">
            <div
              v-for="(faq, index) in faqs"
              :key="index" 
              class="faq-item animate fade-in-up" 
              :class="[
                `animate--delay-${Math.min(index, 3)}`,
                { 'is-active': activeIndex === index }
              ]"
              @click="toggleFaq(index)"
            >
              <div class="faq-question">
                <h3>{{ $t(faq.question) }}</h3>
                <div class="faq-icon">
                  <i
                    class="fas"
                    :class="activeIndex === index ? 'fa-minus' : 'fa-plus'"
                  />
                </div>
              </div>
              <div
                v-show="activeIndex === index"
                class="faq-answer"
              >
                <p>{{ $t(faq.answer) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';

const activeIndex = ref(0); // First one open by default

const faqs = [
  {
    question: 'landing.faq-item1-question',
    answer: 'landing.faq-item1-answer',
  },
  {
    question: 'landing.faq-item2-question',
    answer: 'landing.faq-item2-answer',
  },
  {
    question: 'landing.faq-item3-question',
    answer: 'landing.faq-item3-answer',
  },
  {
    question: 'landing.faq-item4-question',
    answer: 'landing.faq-item4-answer',
  },
  {
    question: 'landing.faq-item5-question',
    answer: 'landing.faq-item5-answer',
  },
  {
    question: 'landing.faq-item6-question',
    answer: 'landing.faq-item6-answer',
  },
];

const toggleFaq = (index) => {
  if (activeIndex.value === index) {
    // If it's already active, allow closing it
    activeIndex.value = -1;
  } else {
    activeIndex.value = index;
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.faq-section {
  background-color: var(--color-background, #fff);
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.faq-list {
  margin-top: 2rem;
}

.faq-item {
  background-color: var(--color-background, white);
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
  
  &.is-active {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    border-left: 3px solid $indiefy-blue;
    
    .faq-question {
      h3 {
        color: $indiefy-blue;
      }
    }
  }
}

.faq-question {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
    color: var(--color-text, #333);
    transition: color 0.3s ease;
  }
  
  .faq-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $indiefy-blue;
    transition: transform 0.3s ease;
    margin-left: 1rem;
    flex-shrink: 0;
  }
}

.faq-answer {
  padding: 0 1.5rem 1.5rem;
  
  p {
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
    color: var(--color-text-light, #666);
  }
}

// Schema.org markup for FAQs (improves SEO with rich results)
// Note: This is included for styling purposes but requires server-side or build-time implementation
.faq-schema {
  display: none;
}
</style>